import { Link } from "react-router-dom";
import { cn } from "../../lib/cn";
import { fmtDateTime, fmtInt } from "../../lib/format";
import { PHASE_LABEL, TRACE_HELP, TRACE_KINDS, TRACE_LABEL, type TraceItem, type TraceKind } from "./model";
import { RunStatusChip } from "./RunsTable";

const th = "px-3 py-2.5 text-left font-mono text-[11px] font-bold uppercase tracking-[0.12em] text-mu";
const td = "px-3 py-3 align-top";

/** Selector de tipo de discrepancia, con su contador. */
export function TraceKinds({ counts, kind, onPick }: { counts: Record<TraceKind, number>; kind: TraceKind; onPick: (k: TraceKind) => void }) {
  return (
    <div className="flex flex-wrap gap-2" role="tablist" aria-label="Tipo de discrepancia">
      {TRACE_KINDS.map((k) => (
        <button key={k} role="tab" aria-selected={kind === k} type="button" onClick={() => onPick(k)}
          className={cn("min-h-11 cursor-pointer rounded-[10px] border px-3 text-[13px] font-medium", kind === k ? "border-ac text-ok" : "border-ln2 text-tx hover:bg-hv")}>
          {TRACE_LABEL[k]} <span className={cn("ml-1 font-mono", counts[k] > 0 ? "text-wn" : "text-mu")}>{counts[k]}</span>
        </button>))}
    </div>
  );
}

/** Lista de discrepancias: se consulta aunque no exista fila de ejecución (p. ej. consumo huérfano). */
export function TraceTable({ kind, items, onOpenRun }: { kind: TraceKind; items: TraceItem[]; onOpenRun: (id: string) => void }) {
  return (
    <div>
      <p className="m-0 mb-3 max-w-[80ch] text-[13px] text-mu">{TRACE_HELP[kind]}</p>
      <div className="overflow-x-auto rounded-card border border-ln bg-sf">
        <table className="w-full border-collapse text-[13px]">
          <thead className="border-b border-ln"><tr>
            <th className={th}>Misión</th><th className={cn(th, "hidden md:table-cell")}>Repositorio</th>
            <th className={th}>{kind === "usage_without_run" || kind === "agent_without_mission" ? "Agente" : "Run"}</th>
            {kind === "usage_without_run" && <th className={th}>Consumo</th>}
            <th className={cn(th, "hidden lg:table-cell")}>Fecha</th><th className={cn(th, "hidden lg:table-cell")}>Origen</th>
          </tr></thead>
          <tbody>
            {items.map((i) => (
              <tr key={i.id} className="border-t border-ln first:border-t-0">
                <td className={td}>{i.missionId ? <Link to={`/missions/${i.missionId}`} className="font-mono font-semibold text-ok hover:underline">{i.missionKey}</Link> : <span className="text-mu">sin misión</span>}</td>
                <td className={cn(td, "hidden break-words font-mono text-[12px] md:table-cell")}>{i.repository ?? "—"}</td>
                <td className={td}>
                  {i.runId ? (<div className="flex flex-wrap items-center gap-2"><span>{PHASE_LABEL[i.purpose ?? ""] ?? i.purpose}</span>{i.status && <RunStatusChip status={i.status} className="h-6 text-[12px]" />}
                    <button type="button" onClick={() => onOpenRun(i.runId!)} className="min-h-9 cursor-pointer text-ok hover:underline">Ver run</button></div>)
                    : (<div>{i.agentName ?? "sin agente"}<div className="font-mono text-[11px] text-mu">{i.provider}{i.externalId ? ` · ${i.externalId}` : ""}</div></div>)}
                </td>
                {kind === "usage_without_run" && <td className={td}><span className="font-mono">{fmtInt(i.tokens ?? 0)}</span> tokens<div className="text-[12px] text-mu">{i.completeness === "complete" ? "final" : "parcial/pendiente"}</div></td>}
                <td className={cn(td, "hidden whitespace-nowrap lg:table-cell")}>{fmtDateTime(i.at)}</td>
                <td className={cn(td, "hidden font-mono text-[12px] lg:table-cell")}>{i.source ?? i.provider ?? "—"}</td>
              </tr>))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
